/**
 * Spark Activation API (vercel-auth-functions)
 *
 * Handles the server-side activation step that sits between access code
 * validation and Supabase OTP.
 *
 * TODO: Vercel base URLs for local, dev, qa, pr, and prod still point to dev — update when URLs are available.
 */
import { envSwitch } from './environmentUtils';
import { logError } from './logError';

import type { LineOfBusiness } from './constants';

const VERCEL_BASE_URL = envSwitch({
  local: 'https://vercel-auth-functions.vercel.app',
  dev: 'https://vercel-auth-functions.vercel.app',
  qa: 'https://vercel-auth-functions.vercel.app',
  pr: 'https://vercel-auth-functions.vercel.app',
  stage: 'https://vercel-auth-functions-stage.vercel.app',
  prod: 'https://vercel-auth-functions-prod.vercel.app',
});

interface ActivatePhoneParams {
  phone: string;
  accessCode?: string;
  product?: string;
  lob?: LineOfBusiness;
  organizationSlug?: string;
}

interface ActivatePhoneResponse {
  success: boolean;
  message: string;
  limbix_user_id: string;
}

interface ActivateConfirmParams {
  supabaseUserId: string;
  accessToken: string;
  lob?: LineOfBusiness;
}

// short-lived exchange token for native app session handoff.
interface AuthorizeRawResponse {
  code: string;
}

interface AuthorizeResponse {
  exchangeToken: string;
}

/**
 * Calls POST /api/spark/{lob}/activate/confirm to flip user_app_entitlements.status → active.
 * Must be called AFTER supabase.auth.verifyOtp() succeeds.
 */
export const activateSparkConfirm = async (
  params: ActivateConfirmParams,
): Promise<void> => {
  const lob = params.lob ?? 'wellness';
  const url = `${VERCEL_BASE_URL}/api/spark/${lob}/activate/confirm`;
  const payload = { supabase_user_id: params.supabaseUserId };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.accessToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    logError(
      '[activateSparkConfirm]',
      `failed with status: ${response.status}`,
      undefined,
      { status: response.status, url },
    );
    throw new Error(`Spark confirm failed: ${response.status}`);
  }
};

/**
 * Calls POST /api/auth/authorize to generate a short-lived exchange token.
 * Must be called AFTER refreshSession() — access_token must contain status: 'active'.
 * The returned exchange_token expires in 30 seconds.
 */
export const requestAuthCode = async (
  accessToken: string,
  refreshToken: string,
): Promise<AuthorizeResponse> => {
  const url = `${VERCEL_BASE_URL}/api/auth/authorize`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    logError('[requestAuthCode]', `failed with status: ${response.status}`, undefined, { status: response.status });
    throw new Error(`Auth authorize failed: ${response.status}`);
  }

  const data: AuthorizeRawResponse = await response.json();
  if (!data.code) {
    throw new Error('Auth authorize returned no token');
  }
  return { exchangeToken: data.code };
};

/**
 * Calls POST /api/spark/{lob}/activate/phone to register the user server-side.
 * Must be called BEFORE supabase.auth.signInWithOtp()
 */
export const activateSparkPhone = async (
  params: ActivatePhoneParams,
): Promise<ActivatePhoneResponse> => {
  const lob = params.lob ?? 'wellness';
  const url = `${VERCEL_BASE_URL}/api/spark/${lob}/activate/phone`;
  const payload: Record<string, string> = {
    phone: params.phone,
  };

  if (params.accessCode) {
    payload.access_code = params.accessCode;
  } else if (params.organizationSlug) {
    payload.organization_slug = params.organizationSlug;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    logError(
      '[activateSparkPhone]',
      `failed with status: ${response.status}`,
      undefined,
      { status: response.status, url },
    );
    throw new Error(`Spark activation failed: ${response.status}`);
  }

  return response.json();
};

interface ActivateEmailParams {
  email: string;
  product: string;
  accessCode?: string;
  organizationSlug?: string;
  lob?: LineOfBusiness;
}

interface ActivateEmailResponse {
  success: boolean;
  limbix_user_id: string;
}

/**
 * Calls POST /api/{product}/{lob}/activate/email to trigger a magic link email.
 *
 * Two flows:
 * - Access code flow: send { email, access_code }
 * - Direct access flow: send { email, organization_slug }
 */
export const activateEmail = async (
  params: ActivateEmailParams,
): Promise<ActivateEmailResponse> => {
  const lob = params.lob ?? 'wellness';
  const url = `${VERCEL_BASE_URL}/api/${params.product}/${lob}/activate/email`;

  const payload: Record<string, string> = {
    email: params.email,
  };

  if (params.accessCode) {
    payload.access_code = params.accessCode;
  } else if (params.organizationSlug) {
    payload.organization_slug = params.organizationSlug;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    logError(
      '[activateEmail]',
      `failed: ${response.status}`,
      undefined,
      { status: response.status, error: body.error, url },
    );
    throw new Error(body.error || `Email activation failed: ${response.status}`);
  }

  return response.json();
};

/**
 * Calls POST /api/auth/login for returning user login.
 * Handles both phone and email — Vercel checks Supabase, migrates from Cortex
 * if needed, then triggers OTP/magic link server-side.
 *
 * Returns a uniform response regardless of whether user exists (HIPAA).
 */
export const loginUser = async (
  params: { phone: string; product: string } | { email: string; product: string },
): Promise<void> => {
  const url = `${VERCEL_BASE_URL}/api/auth/login`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    console.error('[loginUser] failed:', response.status, body.error);
    throw new Error(body.error || `Login failed: ${response.status}`);
  }
};
